import { inject, NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { MsalGuard, MsalRedirectComponent } from '@azure/msal-angular';

import {
  LoginComponent,
  LoginFailedComponent,
  LogoutComponent,
  PageNotAllowedComponent,
  PageNotFoundComponent,
  SecurityProfilesComponent,
} from './core';
import { AdminChildGuard, HasPermissionGuard, featureFlagGuard } from './shared/guards';
import { Features, PermissionType } from './shared/models/permission/permission.model';
import { alreadyLoggedInGuard, securityProfileAuthTokenGuard } from './guards';
import { FeatureName } from './core/state';

const routes: Routes = [
  {
    path: '',
    component: LoginComponent,
    canActivate: [alreadyLoggedInGuard],
    pathMatch: 'full',
  },
  { path: 'auth', component: MsalRedirectComponent },
  {
    path: 'clear2',
    pathMatch: 'prefix',
    redirectTo: (redirectData) => {
      const router = inject(Router);
      const newPath =
        redirectData.url.length > 1
          ? '/' +
            redirectData.url
              .slice(1)
              .map((segment) => segment.path)
              .join('/')
          : '/';
      return router.createUrlTree([newPath], { queryParams: redirectData.queryParams });
    },
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
    canActivate: [MsalGuard],
  },
  {
    path: 'demo',
    loadComponent: () => import('./routes/demo/demo.component').then((m) => m.DemoComponent),
  },
  {
    // This route is used to trigger login with a specified security profile
    // and route to navigate to after the user successfully logs in
    path: 'integrated-login',
    loadComponent: () => import('./integrated-login/integrated-login.component').then((m) => m.IntegratedLoginComponent),
  },
  {
    // This route is used in conjunction with the one above to complete the login process
    path: 'integrated-login-callback',
    loadComponent: () => import('./integrated-login/integrated-login.component').then((m) => m.IntegratedLoginComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'fee-worksheet',
    loadChildren: () => import('./fee-worksheet-15/fee-worksheet.module').then((m) => m.FeeWorksheetModule),
    canActivate: [MsalGuard],
    data: {
      isPipeline: true,
    },
  },
  {
    path: 'feature-flag-manager',
    loadComponent: () => import('./feature-flag-manager/feature-flag-manager.component').then((m) => m.FeatureFlagManagerComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'login-failed',
    component: LoginFailedComponent,
  },
  {
    path: 'logout',
    component: LogoutComponent,
  },
  {
    path: 'security-profiles',
    component: SecurityProfilesComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'urla2',
    loadChildren: () => import('./urla2/urla2.module').then((m) => m.Urla2Module),
    canActivate: [MsalGuard, HasPermissionGuard.forPermission(Features.Loan, PermissionType.View)],
  },
  {
    path: 'pipeline',
    loadChildren: () => import('./pipeline/pipeline.module').then((m) => m.PipelineModule),
    canActivate: [MsalGuard, securityProfileAuthTokenGuard],
  },
  {
    path: 'marketing-automations',
    loadChildren: () => import('./marketing-automations/marketing-automations.module').then((m) => m.MarketingAutomationsModule),
    canActivate: [MsalGuard, securityProfileAuthTokenGuard, featureFlagGuard(FeatureName.OpportunityMarketingAutomations)],
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin-routing.module').then((m) => m.AdminRoutingModule),
    canActivate: [MsalGuard, AdminChildGuard.forChild(['/admin/user-profile', '/admin/add-remove-profiles', '/admin/du-permissions'])],
  },
  {
    path: 'clear1',
    loadChildren: () => import('./external-host/external-host.module').then((m) => m.ExternalHostModule),
    canActivate: [MsalGuard],
  },
  {
    path: 'scenariopricing',
    loadChildren: () => import('./scenario-pricing/scenario-pricing.module').then((m) => m.ScenarioPricingModule),
    canActivate: [MsalGuard],
  },
  {
    path: 'file-viewer',
    loadComponent: () => import('./file-viewer/file-viewer.component').then((m) => m.FileViewerComponent),
    canActivate: [MsalGuard],
  },
  {
    path: '404',
    component: PageNotFoundComponent,
  },
  {
    path: '405',
    component: PageNotAllowedComponent,
  },
  { path: '**', redirectTo: '404' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload', enableTracing: false })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
